import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import Navbar from './Navbar';
import TrialNotification from './TrialNotification';
import API_ENDPOINTS from '../config';
import { authenticatedFetch, authenticatedFetchJson } from '../utils/api.js';

export default function Upgrade() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [companyData, setCompanyData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCompanyData();
    fetchUserInfo();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const data = await authenticatedFetchJson(API_ENDPOINTS.PROFILE);
      if (data.company) {
        setCompanyData(data.company);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const data = await authenticatedFetchJson(API_ENDPOINTS.PROFILE_USER);
      if (data.user) {
        setUserInfo(data.user);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const handleGenerateInvoice = async (planType, amount) => {
    if (!companyData) {
      alert(t('upgrade.companyDataError'));
      return;
    }

    setLoading(true);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.INVOICE_CREATE, {
        method: 'POST',
        body: JSON.stringify({
          companyId: companyData.id,
          amount: amount,
          description: t('upgrade.annualSubscription', { planType }),
          subscriptionDays: 365
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate invoice');
      }

      const data = await response.json();
      alert(data.message);
      navigate('/invoice');
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert(t('upgrade.invoiceEmailMessage'));
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  if (userInfo && userInfo.role !== 'admin') {
    return (
      <div style={{ minHeight: '100vh', background: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
            {t('upgrade.accessDenied')}
          </h1>
          <p style={{ fontSize: '1.1rem', color: colors.textSecondary, marginBottom: '2rem', transition: 'color 0.3s ease' }}>
            {t('upgrade.accessDeniedMessage')}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
          >
            {t('upgrade.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.background, transition: 'background-color 0.3s ease' }}>
      <TrialNotification companyData={companyData} />
      <Navbar scriptMode="latin" onScriptModeChange={() => {}} />
      
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: colors.buttonSecondaryBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: colors.buttonSecondaryText,
              marginBottom: '1rem',
              transition: 'background-color 0.2s, border-color 0.2s, color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.buttonSecondaryHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.buttonSecondaryBg}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>
            </svg>
            {t('upgrade.backToDashboard')}
          </button>
          
          <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: colors.text, margin: '0 0 1rem 0', transition: 'color 0.3s ease' }}>
            {t('upgrade.title')}
          </h1>
          <p style={{ fontSize: '1.1rem', color: colors.textSecondary, margin: 0, transition: 'color 0.3s ease' }}>
            {t('upgrade.subtitle')}
          </p>
        </div>

        {/* Pricing Cards */}
        <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginBottom: '3rem' }}>
          {/* Basic Plan */}
          <div style={{
            background: colors.card,
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: `0 4px 20px ${colors.shadowHover}`,
            border: `2px solid ${colors.border}`,
            textAlign: 'center',
            transition: 'background-color 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.text, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
              {t('upgrade.basicPlan')}
            </h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: colors.primary, marginBottom: '0.5rem', transition: 'color 0.3s ease' }}>
              34899RSD
            </div>
            <div style={{ fontSize: '1rem', color: colors.textSecondary, marginBottom: '2rem', transition: 'color 0.3s ease' }}>
              {t('upgrade.yearly')}
            </div>
            
            <ul style={{ textAlign: 'left', marginBottom: '2rem', padding: 0, listStyle: 'none' }}>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.text, transition: 'color 0.3s ease' }}>
                <span style={{ color: colors.success, fontSize: '1.2rem', transition: 'color 0.3s ease' }}>✓</span>
                {t('upgrade.unlimitedUploads')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.text, transition: 'color 0.3s ease' }}>
                <span style={{ color: colors.success, fontSize: '1.2rem', transition: 'color 0.3s ease' }}>✓</span>
                {t('upgrade.upTo3Accounts')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.text, transition: 'color 0.3s ease' }}>
                <span style={{ color: colors.success, fontSize: '1.2rem', transition: 'color 0.3s ease' }}>✓</span>
                {t('upgrade.taurusExport')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.text, transition: 'color 0.3s ease' }}>
                <span style={{ color: colors.success, fontSize: '1.2rem', transition: 'color 0.3s ease' }}>✓</span>
                {t('upgrade.emailSupport')}
              </li>
            </ul>
            
            <button
              onClick={() => handleGenerateInvoice(t('upgrade.basicPlan'), 2899)}
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? colors.textTertiary : colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = colors.primaryHover)}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = colors.primary)}
            >
              {loading ? t('upgrade.generating') : t('upgrade.selectBasic')}
            </button>
          </div>

          {/* Pro Plan */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            padding: '2rem',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            border: '2px solid #667eea',
            textAlign: 'center',
            color: 'white',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#f59e0b',
              color: 'white',
              padding: '4px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 600
            }}>
              {t('upgrade.mostPopular')}
            </div>
            
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem' }}>
              {t('upgrade.proPlan')}
            </h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>
              69999RSD
            </div>
            <div style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '2rem' }}>
            {t('upgrade.yearly')}
            </div>
            
            <ul style={{ textAlign: 'left', marginBottom: '2rem', padding: 0, listStyle: 'none' }}>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                {t('upgrade.allFromBasic')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                {t('upgrade.unlimitedMembers')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                {t('upgrade.prioritySupport')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                {t('upgrade.customModels')}
              </li>
              <li style={{ padding: '0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>✓</span>
                {t('upgrade.removeCirilica')}
              </li>
            </ul>
            
            <button
              onClick={() => handleGenerateInvoice(t('upgrade.proPlan'), 5999)}
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.3)')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
            >
              {loading ? t('upgrade.generating') : t('upgrade.selectPro')}
            </button>
          </div>
        </div>

        {/* Features Section */}
        <div style={{
          background: colors.card,
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: `0 4px 20px ${colors.shadowHover}`,
          marginBottom: '2rem',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.text, marginBottom: '1.5rem', textAlign: 'center', transition: 'color 0.3s ease' }}>
            {t('upgrade.whyUpgrade')}
          </h2>
          
          <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto'
              }}>
                <span style={{ fontSize: '24px', color: 'white' }}>🚀</span>
              </div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem', transition: 'color 0.3s ease' }}>
                {t('upgrade.unlimitedUsage')}
              </h4>
              <p style={{ color: colors.textSecondary, fontSize: '14px', transition: 'color 0.3s ease' }}>
                {t('upgrade.unlimitedUsageDesc')}
              </p>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto'
              }}>
                <span style={{ fontSize: '24px', color: 'white' }}>👥</span>
              </div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem', transition: 'color 0.3s ease' }}>
                {t('upgrade.teamCollaboration')}
              </h4>
              <p style={{ color: colors.textSecondary, fontSize: '14px', transition: 'color 0.3s ease' }}>
                {t('upgrade.teamCollaborationDesc')}
              </p>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto'
              }}>
                <span style={{ fontSize: '24px', color: 'white' }}>🛡️</span>
              </div>
              <h4 style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.text, marginBottom: '0.5rem', transition: 'color 0.3s ease' }}>
                {t('upgrade.prioritySupportTitle')}
              </h4>
              <p style={{ color: colors.textSecondary, fontSize: '14px', transition: 'color 0.3s ease' }}>
                {t('upgrade.prioritySupportDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div style={{
          background: colors.backgroundLight,
          borderRadius: '16px',
          padding: '2rem',
          textAlign: 'center',
          border: `1px solid ${colors.border}`,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.text, marginBottom: '1rem', transition: 'color 0.3s ease' }}>
            {t('upgrade.haveQuestions')}
          </h3>
          <p style={{ color: colors.textSecondary, marginBottom: '1.5rem', transition: 'color 0.3s ease' }}>
            {t('upgrade.contactMessage')}
          </p>
          <a 
            href="mailto:nemanja@lizzardsolutions.com"
            style={{ textDecoration: 'none' }}
          >
            <button
              style={{
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
            >
              {t('upgrade.contactUs')}
            </button>
          </a>
        </div>
      </div>
    </div>
  );
} 